/**
 * THE SHARE BUTTON, for every room that has one.
 *
 * One presenter at a time is enforced in the media client (`shareArbiter` +
 * `livekitLocalControls`), which is what makes it true even when nothing is
 * mounted to watch. This hook is the part a person interacts with: it opens the
 * source picker, turns a refusal into "Ganesh is presenting — take over?", and
 * says out loud when this client's own share was stopped because somebody
 * else's claim won.
 *
 * Shared by the meeting room and the call room deliberately. They had two
 * copies of the start-share `catch` already, and the second copy is where the
 * two behaviours drift.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@cyphex/ui';
import type { MediaClient, MediaSnapshot, ScreenShareHint } from '../media/contract';
import type { SharePresenter } from '../media/shareArbiter';

export interface ShareController {
  /** Whoever owns the stage right now — possibly this client. */
  readonly presenter: SharePresenter | null;
  readonly isLocalPresenter: boolean;
  /** This client is publishing a share, owner or not (the losing beat of a race). */
  readonly isPublishing: boolean;
  /**
   * A display capture is OPEN on this machine — what the OS thinks, which is
   * not always what the SFU thinks. See `MediaSnapshot.shareCapturing`.
   */
  readonly isCapturing: boolean;
  /** A host revoked this participant's screen share; only a host can give it back. */
  readonly shareDisabled: boolean;
  /** This client's share is carrying system audio. */
  readonly withAudio: boolean;
  /**
   * The presenter turned on "Include computer sound" for this share. Kept
   * apart from `withAudio` so the presenting bar can tell "you asked for sound
   * and this source has none" from "you did not ask for any".
   */
  readonly soundRequested: boolean;
  readonly pickerOpen: boolean;
  /** Set when a start was refused; drives the take-over dialog. */
  readonly blockedBy: SharePresenter | null;
  setPickerOpen(open: boolean): void;
  /** Share, or stop sharing — the toolbar's one button. */
  toggle(): void;
  /** "Take over" on the presenting bar: the picker, pre-armed. */
  requestTakeOver(): void;
  /** `computerSound` is the picker's "Include computer sound"; off unless set. */
  start(hint: ScreenShareHint, computerSound?: boolean): void;
  confirmTakeOver(): void;
  cancelTakeOver(): void;
  stop(): void;
}

export function useShareController(
  client: MediaClient,
  snapshot: MediaSnapshot,
): ShareController {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [blockedBy, setBlockedBy] = useState<SharePresenter | null>(null);
  const [audioCaptured, setAudioCaptured] = useState(false);
  const [soundAsked, setSoundAsked] = useState(false);
  /**
   * Whether the NEXT start is a deliberate take-over. A ref, not state: it is
   * read inside the picker's callback and must not re-render anything on its
   * way there, and a stale render must never be able to arm one by accident.
   */
  const armed = useRef(false);
  const pendingHint = useRef<ScreenShareHint>('detail');
  /** The sound choice a refused start was made with, for the take-over. */
  const pendingSound = useRef(false);
  /** The last yield already announced, so a re-render cannot repeat it. */
  const announced = useRef(0);

  const { screenShareOwnerId, localId, participants, shareStopped, shareCapturing } = snapshot;
  const isLocalPresenter = screenShareOwnerId !== null && screenShareOwnerId === localId;
  const isPublishing = participants.some((p) => p.isLocal && p.sharing);
  const shareDisabled = participants.some((p) => p.isLocal && p.shareDisabled);

  const presenter = useMemo<SharePresenter | null>(() => {
    if (screenShareOwnerId === null) return null;
    const owner = participants.find((p) => p.id === screenShareOwnerId);
    return { id: screenShareOwnerId, name: owner?.name ?? 'Someone' };
  }, [participants, screenShareOwnerId]);

  /**
   * SAY IT WHEN A SHARE IS STOPPED BY SOMETHING THAT IS NOT THIS BUTTON.
   *
   * The provider has already released the capture by the time this runs — that
   * is not negotiable and does not wait for a render, and the provider does not
   * raise this notice at all unless the capture really was released. This is
   * only the sentence that stops it being a mystery.
   *
   * The two reasons need two sentences. Telling somebody a host stopped them
   * that "Someone else is presenting now" points them at a stage that is empty
   * and at a take-over that will be refused; telling them nothing at all is how
   * a screen goes on being captured by somebody who thinks it is not.
   */
  useEffect(() => {
    if (shareStopped === null || shareStopped.at === announced.current) return;
    announced.current = shareStopped.at;
    setAudioCaptured(false);
    setSoundAsked(false);
    if (shareStopped.reason === 'host') {
      toast.warning('A host stopped your screen share.', {
        description: 'You cannot share again until a host allows it.',
      });
      return;
    }
    toast.warning(`${shareStopped.winnerName ?? 'Someone else'} is presenting now.`, {
      description: 'Your screen share was stopped. Only one person can present at a time.',
    });
  }, [shareStopped]);

  const begin = useCallback(
    (hint: ScreenShareHint, takeOver: boolean, computerSound: boolean) => {
      client
        .startScreenShare(hint, takeOver, { computerSound })
        .then((result) => {
          if (result.status === 'blocked') {
            pendingHint.current = hint;
            pendingSound.current = computerSound;
            setBlockedBy(result.presenter);
            return;
          }
          if (result.status === 'stopped') {
            setAudioCaptured(false);
            setSoundAsked(false);
            /*
             * ONE EVENT, ONE SENTENCE.
             *
             * Stopped inside its own publish window — by the toolbar, or by the
             * floating bar the browser puts over the shared screen. The media
             * client has already taken the track back down and released the
             * capture; this is the sentence that stops it being a mystery that
             * the share never appeared.
             *
             * But `stopped` used to carry nothing, so the same sentence covered
             * a HOST's Stop and a take-over as well — printed underneath the
             * true sentence the effect above had already produced from
             * `shareStopped`, for the very same event. Those two arrive with a
             * reason and a name attached and are announced there, for every
             * share and not only one caught mid-start. Only a stop this person
             * made themselves has nothing else to explain it.
             */
            if (result.stoppedBy !== 'self') return;
            toast.info('Screen sharing stopped.', {
              description: 'You stopped it while it was still starting.',
            });
            return;
          }
          if (result.status === 'denied') {
            /* No take-over dialog: there is nobody to take it from, and the
               only thing that lifts this is a host pressing Allow. */
            toast.warning('A host turned off screen sharing for you.', {
              description: 'Ask a host to allow it.',
            });
            return;
          }
          setAudioCaptured(result.withAudio);
          setSoundAsked(computerSound);
        })
        .catch((error: unknown) => {
          /* Dismissing the chooser is a CHOICE and needs no toast; anything
             else is a genuine failure and must not be reported as though the
             user did it. Capture constraints (the 1080p cap) can reject here. */
          const name = error instanceof Error ? error.name : '';
          if (name === 'NotAllowedError' || name === 'AbortError') return;
          toast.error('Could not start screen sharing.');
        });
    },
    [client],
  );

  const stop = useCallback(() => {
    setAudioCaptured(false);
    setSoundAsked(false);
    void client.stopScreenShare();
  }, [client]);

  return {
    presenter,
    isLocalPresenter,
    isPublishing,
    isCapturing: shareCapturing,
    shareDisabled,
    withAudio: isPublishing && audioCaptured,
    soundRequested: isPublishing && soundAsked,
    pickerOpen,
    blockedBy,

    setPickerOpen: (open) => {
      if (!open) armed.current = false;
      setPickerOpen(open);
    },

    /**
     * SHARE, OR STOP SHARING — and "stop" has to mean a capture that EXISTS,
     * not a publication the SFU happens to be carrying.
     *
     * `isPublishing` alone was the trap. It is the SFU's answer, and the SFU's
     * answer goes false while a capture is still running in at least three
     * ways: a provider rejoin republishes mic and camera and not the share, a
     * host's Stop removes the publication and cannot touch the capture, and a
     * publish refused at 403 unwinds one and not the other. In every one of
     * them this button read "Share" — and pressing it opened the chooser and
     * started a SECOND capture rather than ending the first, with no path back
     * to the orphan, because `stop()` was unreachable while `isPublishing` was
     * false.
     *
     * So the button follows the OS, and the OS indicator can always be turned
     * off from inside the app. That is the whole point of it.
     */
    toggle: () => {
      if (isPublishing || shareCapturing) {
        stop();
        return;
      }
      if (shareDisabled) {
        toast.warning('A host turned off screen sharing for you.', {
          description: 'Ask a host to allow it.',
        });
        return;
      }
      armed.current = false;
      setPickerOpen(true);
    },

    requestTakeOver: () => {
      armed.current = true;
      setPickerOpen(true);
    },

    /**
     * The layout is NOT touched here. `useShareLayout` moves every participant
     * — presenter included — to the presentation stage when a share appears on
     * the roster, and back when it goes. Switching on the button press instead
     * would show the presenter an empty "Nobody is sharing" stage for as long
     * as the browser's picker is open, and leave them there if they cancel it.
     */
    start: (hint, computerSound = false) => {
      const takeOver = armed.current;
      armed.current = false;
      setPickerOpen(false);
      begin(hint, takeOver, computerSound);
    },

    /* The take-over is the SAME share the person asked for, sound choice
       included: re-asking without it would quietly drop a video's sound, and
       re-asking with it switched on would capture what they never chose. */
    confirmTakeOver: () => {
      const hint = pendingHint.current;
      setBlockedBy(null);
      begin(hint, true, pendingSound.current);
    },

    cancelTakeOver: () => setBlockedBy(null),
    stop,
  };
}
